import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  ReactNode,
  Dispatch,
} from 'react';
import { GridFilterItem } from '@mui/x-data-grid-pro';
import { ArchivePoint } from '@src/types/Archivist';
import { PaginationModel } from '@src/types/pagination/PaginationModel';
import {
  DataPointSearchState,
  DataPointSearchAction,
  DataPointSearchActionType,
  FilterField,
  SelectedDataPoint,
} from '../types';
import { dataPointSearchReducer, initialState } from './dataPointSearchReducer';

interface DataPointSearchContextValue {
  state: DataPointSearchState;
  dispatch: Dispatch<DataPointSearchAction>;
  actions: {
    setFilter: (field: FilterField, value: string) => void;
    setGridFilters: (filters: GridFilterItem[]) => void;
    clearFilters: () => void;
    setSearchText: (text: string) => void;
    submitSearch: () => void;
    clearSearch: () => void;
    setPagination: (pagination: Partial<PaginationModel>) => void;
    syncPageSelection: (
      currentPageRows: ArchivePoint[],
      selectedIds: string[],
    ) => void;
    clearSelection: () => void;
    resetState: () => void;
    getSelectedDataPoints: () => SelectedDataPoint[];
  };
}

const DataPointSearchContext = createContext<
  DataPointSearchContextValue | undefined
>(undefined);

interface DataPointSearchProviderProps {
  children: ReactNode;
}

export const DataPointSearchProvider: React.FC<
  DataPointSearchProviderProps
> = ({ children }) => {
  const [state, dispatch] = useReducer(dataPointSearchReducer, initialState);

  const setFilter = useCallback((field: FilterField, value: string) => {
    dispatch({
      type: DataPointSearchActionType.SET_FILTER,
      payload: { field, value },
    });
  }, []);

  const setGridFilters = useCallback((filters: GridFilterItem[]) => {
    dispatch({
      type: DataPointSearchActionType.SET_GRID_FILTERS,
      payload: filters,
    });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: DataPointSearchActionType.CLEAR_FILTERS });
  }, []);

  const setSearchText = useCallback((text: string) => {
    dispatch({
      type: DataPointSearchActionType.SET_SEARCH_TEXT,
      payload: text,
    });
  }, []);

  const submitSearch = useCallback(() => {
    dispatch({ type: DataPointSearchActionType.SUBMIT_SEARCH });
  }, []);

  const clearSearch = useCallback(() => {
    dispatch({ type: DataPointSearchActionType.CLEAR_SEARCH });
  }, []);

  const setPagination = useCallback((pagination: Partial<PaginationModel>) => {
    dispatch({
      type: DataPointSearchActionType.SET_PAGINATION,
      payload: pagination,
    });
  }, []);

  const syncPageSelection = useCallback(
    (currentPageRows: ArchivePoint[], selectedIds: string[]) => {
      dispatch({
        type: DataPointSearchActionType.SYNC_PAGE_SELECTION,
        payload: { currentPageRows, selectedIds },
      });
    },
    [],
  );

  const clearSelection = useCallback(() => {
    dispatch({ type: DataPointSearchActionType.CLEAR_SELECTION });
  }, []);

  const resetState = useCallback(() => {
    dispatch({ type: DataPointSearchActionType.RESET_STATE });
  }, []);

  const getSelectedDataPoints = useCallback((): SelectedDataPoint[] => {
    return Array.from(state.selection.values())
      .filter((point) => point && point.topic)
      .map((point) => ({
        topic: point.topic,
        displayName: point.pointName || point.topic || 'Unknown',
        pointName: point.pointName || '',
        deviceType: point.deviceType || '',
        siteId: point.siteId || '',
        deviceId: point.deviceId || '',
        parentDeviceId: point.parentDeviceId || '',
        parentDeviceType: point.parentDeviceType || '',
        dataType: point.dataType || '',
      }));
  }, [state.selection]);

  const actions = useMemo(
    () => ({
      setFilter,
      setGridFilters,
      clearFilters,
      setSearchText,
      submitSearch,
      clearSearch,
      setPagination,
      syncPageSelection,
      clearSelection,
      resetState,
      getSelectedDataPoints,
    }),
    [
      setFilter,
      setGridFilters,
      clearFilters,
      setSearchText,
      submitSearch,
      clearSearch,
      setPagination,
      syncPageSelection,
      clearSelection,
      resetState,
      getSelectedDataPoints,
    ],
  );

  const value = useMemo(() => ({ state, dispatch, actions }), [state, actions]);

  return (
    <DataPointSearchContext.Provider value={value}>
      {children}
    </DataPointSearchContext.Provider>
  );
};

export const useDataPointSearchContext = (): DataPointSearchContextValue => {
  const context = useContext(DataPointSearchContext);
  if (!context) {
    throw new Error(
      'useDataPointSearchContext must be used within a DataPointSearchProvider',
    );
  }
  return context;
};

export const useDataPointSearchState = (): DataPointSearchState => {
  const { state } = useDataPointSearchContext();
  return state;
};

export const useDataPointSearchActions = () => {
  const { actions } = useDataPointSearchContext();
  return actions;
};
